// Multi-select combobox: type to filter, tick multiple options. Shows a count
// summary in the trigger. Stores the selected values as a string[].
import { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import type { SelectOption } from "./searchable-select";

interface Props {
  values: string[];
  onChange: (values: string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
}

export function MultiSelect({ values, onChange, options, placeholder = "— Chọn —", className }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const selectedSet = new Set(values);

  function toggle(v: string) {
    if (selectedSet.has(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  }

  const summary =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? options.find((o) => o.value === values[0])?.label ?? "1 mục"
        : `Đã chọn ${values.length}`;

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center gap-1 rounded-md border border-input bg-background px-3 text-sm"
      >
        <span className={cn("flex-1 truncate text-left", values.length === 0 && "text-muted-foreground")}>
          {summary}
        </span>
        {values.length > 0 ? (
          <X
            size={14}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
          />
        ) : (
          <ChevronDown size={14} className="shrink-0 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[200px] rounded-md border bg-card shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Gõ để tìm…"
            className="w-full border-b bg-transparent px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-56 overflow-auto py-1 text-sm">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-muted-foreground">Không có kết quả</li>
            )}
            {filtered.map((o) => (
              <li
                key={o.value}
                onMouseDown={(e) => { e.preventDefault(); toggle(o.value); }}
                className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-accent"
              >
                <input type="checkbox" readOnly checked={selectedSet.has(o.value)} className="h-4 w-4" />
                <span className="truncate">{o.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
